import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import api from "../utils/api";
import { useAuth } from "./AuthContext";

interface AppContextType {
  currentApp: string;
  setCurrentApp: React.Dispatch<React.SetStateAction<string>>;
  isLoading: boolean;
  appsAccessData: AppAccess[];
  fetchAppsAccessData: () => Promise<void>;
  app: AppType | null;

  editUserName: string;
  setEditUserName: React.Dispatch<React.SetStateAction<string>>;
  editOrganizationName: string;
  setEditOrganizationName: React.Dispatch<React.SetStateAction<string>>;
}

interface AppAccess {
  user_id: string;
  app_id: string;
  granted_at: string;
}

interface AppType {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  createdby?: string;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const [currentApp, setCurrentApp] = useState("Optenics");
  const [app, setApp] = useState<AppType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [appsAccessData, setAppsAccessData] = useState<AppAccess[]>([]);
  const { isAuthenticated, user } = useAuth();

  const [editUserName, setEditUserName] = useState("");
  const [editOrganizationName, setEditOrganizationName] = useState("");

  const fetchAppsAccessData = useCallback(async () => {
    if (!user?.id || !user?.token) return;

    try {
      const res = await api.post(
        "/core/access/get",
        { user_id: user.id },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      setAppsAccessData(res.data || []);
    } catch (error) {
      console.error("Error fetching app access data:", error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || !user?.token) {
      setIsLoading(false);
      return;
    }

    fetchAppsAccessData();
  }, [fetchAppsAccessData, isAuthenticated, user]);

  return (
    <AppContext.Provider
      value={{
        currentApp,
        setCurrentApp,
        app,
        isLoading,
        appsAccessData,
        fetchAppsAccessData,
        editUserName,
        setEditUserName,
        editOrganizationName,
        setEditOrganizationName,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error("useAppContext must be used within an AppProvider");
  return context;
};
